import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck, Send, BadgeCheck, AlertCircle, FileText, Lock, Clock, Sparkles, LogIn,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  ai_pending_review: { label: 'AI — pending human verification', color: 'bg-amber-100 text-amber-800' },
  ai_only: { label: 'AI — auto-approved', color: 'bg-blue-100 text-blue-800' },
  ai_human_verified: { label: 'Human verified', color: 'bg-emerald-100 text-emerald-800' },
  human_only: { label: 'Answered by Vault Hire', color: 'bg-emerald-100 text-emerald-800' },
  escalated_no_answer: { label: 'Escalated — awaiting human', color: 'bg-rose-100 text-rose-700' },
  blocked_by_filter: { label: 'Out of self-service scope — human follow-up', color: 'bg-purple-100 text-purple-800' },
};

export default function ComplianceCustomerPortal() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signing, setSigning] = useState(false);
  const [signName, setSignName] = useState('');
  const [signTitle, setSignTitle] = useState('');
  const [signAck, setSignAck] = useState(false);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [thread, setThread] = useState([]);

  const loadAccess = useCallback(async () => {
    if (!token) { setError('Missing access token in URL.'); setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/compliance/access?token=${encodeURIComponent(token)}`);
      setAccess(data);
      if (!data.nda_required) {
        const t = await axios.get(`${API}/compliance/questions/mine?token=${encodeURIComponent(token)}`);
        setThread(t.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not access portal');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadAccess(); }, [loadAccess]);

  const handleSignNda = async (e) => {
    e.preventDefault();
    if (!signAck) return;
    setSigning(true);
    try {
      await axios.post(`${API}/compliance/access/agree?token=${encodeURIComponent(token)}`, {
        agreed_name: signName, agreed_title: signTitle,
      });
      await loadAccess();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed to record NDA');
    } finally { setSigning(false); }
  };

  const handleAsk = async (e) => {
    e.preventDefault();
    if (question.trim().length < 5) return;
    setAsking(true);
    try {
      const { data } = await axios.post(
        `${API}/compliance/ask?token=${encodeURIComponent(token)}`,
        { question_text: question }
      );
      setThread((t) => [{ ...data, question_text: question, human_verified: false, answer: data.answer }, ...t]);
      setQuestion('');
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed to send question');
    } finally { setAsking(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500" data-testid="compliance-portal-loading">Loading…</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white flex items-center justify-center p-6" data-testid="compliance-portal-error">
        <div className="max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-rose-400 mx-auto mb-3" />
          <h1 className="text-2xl font-bold mb-2">Access link issue</h1>
          <p className="text-blue-200 text-sm">{error}</p>
          <Link to="/" className="mt-6 inline-block text-blue-300 underline">Back to Vault Hire</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 to-blue-950 text-white border-b border-slate-800">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-400" /> Vault Hire — Compliance Portal
          </Link>
          <div className="text-right text-xs">
            <div className="text-blue-200">{access.org_name}</div>
            <div className="text-slate-400">{access.requester_name}</div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {access.nda_required ? (
          <NdaGate
            text={access.nda_text}
            hash={access.nda_hash}
            version={access.nda_version}
            onSubmit={handleSignNda}
            signName={signName} setSignName={setSignName}
            signTitle={signTitle} setSignTitle={setSignTitle}
            signAck={signAck} setSignAck={setSignAck}
            signing={signing}
          />
        ) : (
          <>
            <div className="bg-white border rounded-xl p-5 mb-5 shadow-sm" data-testid="compliance-ask-form">
              <div className="flex items-center gap-2 mb-1 text-emerald-700 text-xs">
                <BadgeCheck className="w-4 h-4" />
                <span>NDA signed {access.agreed_at?.slice(0, 10)} · {access.question_count} questions on file</span>
              </div>
              <h2 className="text-xl font-semibold mb-1 flex items-center gap-2"><Sparkles className="w-5 h-5 text-blue-600" /> Ask a compliance question</h2>
              <p className="text-xs text-slate-500 mb-3">
                Answers are AI-generated from Vault Hire's source documents and explicitly flagged as
                AI-only, AI + human verified, or human-answered.
              </p>
              <form onSubmit={handleAsk} className="space-y-2">
                <textarea
                  required minLength={5}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value.slice(0, 2000))}
                  placeholder="e.g. Are you SOC 2 Type I certified? What is your incident response timeline?"
                  rows={3}
                  data-testid="compliance-question-input"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">{question.length}/2000</span>
                  <button
                    type="submit"
                    disabled={asking || question.trim().length < 5}
                    data-testid="compliance-ask-submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm flex items-center gap-2 disabled:opacity-50"
                  >
                    <Send className="w-4 h-4" /> {asking ? 'Thinking…' : 'Ask'}
                  </button>
                </div>
              </form>
            </div>

            {thread.length === 0 ? (
              <div className="text-center text-slate-400 text-sm py-12">No questions asked yet. Use the box above to start.</div>
            ) : (
              <ol className="space-y-4" data-testid="compliance-thread">
                {thread.map((q) => {
                  const sl = STATUS_LABEL[q.status] || STATUS_LABEL.ai_pending_review;
                  return (
                    <li key={q.id} data-testid={`compliance-answer-${q.id}`} className="bg-white border rounded-xl p-5 shadow-sm">
                      <div className="text-xs text-slate-500 mb-1 flex items-center gap-2">
                        <Clock className="w-3 h-3" /> {q.created_at?.slice(0, 16).replace('T', ' ')}
                        <span className={`ml-auto px-2 py-0.5 rounded text-xs ${sl.color}`} data-testid={`compliance-status-${q.id}`}>{sl.label}</span>
                      </div>
                      <p className="font-medium text-slate-900 mb-2">Q: {q.question_text}</p>
                      <div className="text-sm text-slate-700 whitespace-pre-wrap">{q.answer}</div>
                      {q.sources?.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1"><FileText className="w-3 h-3" /> Source categories used</p>
                          <ul className="space-y-0.5 text-xs">
                            {q.sources.map((s) => (
                              <li key={s.id} className="flex items-center gap-1.5 text-slate-600">
                                <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-100">{s.visibility}</span>
                                <span>{s.bucket || s.title}</span>
                              </li>
                            ))}
                          </ul>
                          <p className="text-[10px] text-slate-400 mt-1">Source categories shown — exact document titles redacted for IP protection.</p>
                        </div>
                      )}
                      {!q.human_verified && (
                        <div className="mt-3 text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1.5 flex items-center gap-1.5">
                          <AlertCircle className="w-3 h-3" />
                          AI-generated response — final human-verified answer will replace this if/when reviewed.
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </>
        )}
      </main>

      <footer className="container mx-auto px-4 py-8 text-center text-xs text-slate-500">
        <p>All access to this portal is logged and audited.</p>
        <Link to="/trust" className="text-blue-600 hover:underline">Vault Hire Trust Centre</Link>
      </footer>
    </div>
  );
}

function NdaGate({ text, hash, version, onSubmit, signName, setSignName, signTitle, setSignTitle, signAck, setSignAck, signing }) {
  return (
    <div className="bg-white border rounded-xl p-6 shadow-sm" data-testid="compliance-nda-gate">
      <div className="flex items-center gap-2 mb-3 text-blue-700">
        <Lock className="w-5 h-5" />
        <h2 className="text-xl font-semibold">Confidentiality acknowledgement required</h2>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Before we share specifics of Vault Hire's controls, please acknowledge our compliance portal NDA below.
        Your name, title, IP address, and timestamp will be recorded as evidence of acceptance.
      </p>
      <div className="bg-slate-50 border rounded p-4 mb-4 max-h-72 overflow-y-auto text-xs leading-relaxed font-mono whitespace-pre-wrap" data-testid="compliance-nda-text">
        {text}
      </div>
      <p className="text-xs text-slate-400 mb-4">Version {version} · Hash <span className="font-mono">{hash?.slice(0, 12)}…</span></p>
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input
            required minLength={2}
            value={signName} onChange={(e) => setSignName(e.target.value)}
            placeholder="Full name"
            data-testid="compliance-nda-name"
            className="px-3 py-2 border rounded text-sm"
          />
          <input
            value={signTitle} onChange={(e) => setSignTitle(e.target.value)}
            placeholder="Title (optional)"
            data-testid="compliance-nda-title"
            className="px-3 py-2 border rounded text-sm"
          />
        </div>
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={signAck} onChange={(e) => setSignAck(e.target.checked)} data-testid="compliance-nda-ack" className="mt-1" />
          <span>I confirm I have read and accept the terms above, and that I am authorised to bind my organisation.</span>
        </label>
        <button
          type="submit"
          disabled={!signAck || signing || signName.length < 2}
          data-testid="compliance-nda-submit"
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm disabled:opacity-50"
        >
          {signing ? 'Recording…' : 'Sign & enter portal'}
        </button>
      </form>
    </div>
  );
}
